import React from 'react';
import { formatHealthCacheLine, formatTelemetryTenantScope } from '@/lib/healthDetails';

type TelemetryHealth = {
  ok: boolean;
  mode?: string;
  degraded?: boolean;
  freshnessSec?: number | null;
  freshness_sec?: number | null;
  lastEventTs?: string | null;
  last_event_ts?: string | null;
  reason?: string | null;
  updated_at?: string;
  latency_ms?: number;
  cache?: {
    ttl_ms?: number;
    age_ms?: number;
    fresh?: boolean;
  };
};

type TelemetryAgg = {
  ok: boolean;
  mode?: string;
  degraded?: boolean;
  window?: string;
  windowMinutes?: number;
  groupBy?: string;
  items?: Array<{
    tenant?: string;
    totalMessages?: number;
    msgsPerMin?: number;
    windowMinutes?: number;
  }>;
  summary?: {
    tenant?: string | null;
    totalMessages?: number;
    msgsPerMin?: number;
    windowMinutes?: number;
  };
  reason?: string | null;
  updated_at?: string;
  latency_ms?: number;
  cache?: {
    ttl_ms?: number;
    age_ms?: number;
    fresh?: boolean;
  };
};

type Props = {
  health?: TelemetryHealth | null;
  aggregate?: TelemetryAgg | null;
  isLoading?: boolean;
  error?: string | null;
  /** Session tenant (e.g. prod). Used instead of the groupBy placeholder "tenant". */
  sessionTenant?: string | null;
  /** Parent System Health pages already title this section — do not repeat it. */
  showHeading?: boolean;
};

const modeColor: Record<string, string> = {
  normal: 'bg-green-600',
  degraded: 'bg-amber-600',
  down: 'bg-red-600'
};

function normalizeMode(mode?: string) {
  if (!mode) return 'unknown';
  return mode.toLowerCase();
}

export default function TelemetryTiles({
  health,
  aggregate,
  isLoading,
  error,
  sessionTenant,
  showHeading = true,
}: Props) {
  if (error) {
    return <div className="text-sm text-red-600">Telemetry error: {error}</div>;
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading telemetry…</div>;
  }

  const mode = normalizeMode(health?.mode || aggregate?.mode || (health?.ok ? 'normal' : 'degraded'));
  const badgeColor = modeColor[mode] || 'bg-gray-500';
  const freshness = health?.freshnessSec ?? health?.freshness_sec ?? null;
  const lastEvent = health?.lastEventTs || health?.last_event_ts || null;
  const items = aggregate?.items ?? [];
  const tenantScope = formatTelemetryTenantScope({
    groupBy: aggregate?.groupBy,
    sessionTenant,
  });
  const healthCacheLine = formatHealthCacheLine(health?.cache);
  const aggCacheLine = formatHealthCacheLine(aggregate?.cache);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            {showHeading ? (
              <h3 className="text-sm font-medium text-gray-900">Telemetry Health</h3>
            ) : null}
            <div className="text-xs text-gray-500">Updated {health?.updated_at ? new Date(health.updated_at).toLocaleString() : 'recently'}</div>
          </div>
          <span className={`text-xs text-white px-3 py-1 rounded-full ${badgeColor}`}>
            Mode: {mode}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-gray-600">
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="font-medium text-gray-800 mb-1">Freshness</div>
            <div>{freshness != null ? `${freshness}s` : 'n/a'}</div>
            <div className="text-[11px] text-gray-500">Age of latest event</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="font-medium text-gray-800 mb-1">Last Event</div>
            <div>{lastEvent ? new Date(lastEvent).toLocaleString() : 'n/a'}</div>
            <div className="text-[11px] text-gray-500">per tenant: {tenantScope}</div>
          </div>
          {healthCacheLine ? (
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="font-medium text-gray-800 mb-1">Cache</div>
              <div>{healthCacheLine}</div>
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="font-medium text-gray-800 mb-1">Window</div>
              <div>{aggregate?.window || `${aggregate?.windowMinutes ?? 60}m`}</div>
              <div className="text-[11px] text-gray-500">1h aggregate</div>
            </div>
          )}
        </div>
        {health?.reason && (
          <div className="text-xs text-red-600">Reason: {health.reason}</div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">Telemetry Volume · window {aggregate?.window || `${aggregate?.windowMinutes ?? 60}m`}</h3>
          <div className="text-[11px] text-gray-500">
            Updated {aggregate?.updated_at ? new Date(aggregate.updated_at).toLocaleString() : 'recently'}
            {aggCacheLine ? ` · ${aggCacheLine}` : ''}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">Messages</th>
                <th className="px-4 py-2">Msgs / min</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, idx) => (
                  <tr key={`${item.tenant || idx}`} className="border-t text-gray-700">
                    <td className="px-4 py-2">{item.tenant || 'unknown'}</td>
                    <td className="px-4 py-2">{item.totalMessages ?? '—'}</td>
                    <td className="px-4 py-2">{item.msgsPerMin ?? '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={3}>
                    {error
                      ? '1h volume timed out or unavailable — this view, not no traffic.'
                      : !aggregate
                        ? '1h volume not loaded — this view, not no traffic.'
                        : 'No telemetry rows in this 1h agg payload.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {aggregate?.reason && (
          <div className="px-4 py-3 text-xs text-red-600 border-t border-gray-200">Reason: {aggregate.reason}</div>
        )}
      </div>
    </div>
  );
}

export type { TelemetryHealth, TelemetryAgg };
